import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState, useRef } from 'react'
import {
  ContributionImportProgress,
  ContributionImportProgressPhase,
  ContributionImportProgressStatus,
  ContributionJobHistoryItem,
  GetLatestJobResponse,
} from 'enrollment-platform/pages/CustomerManagement/pages/ViewCustomer/tabs/Contribution/types/types'
import {
  getLatestContributionJob,
  getContributionJobStatus,
  getContributionJobs,
  postApplyContributionModel,
} from './methods'
import { queryClient } from 'containers/AppContainer'

export function jobIsActive(job) {
  return ['created', 'active', 'retry'].includes(job.state)
}

const TERMINAL_STATUSES: ContributionImportProgressStatus[] = [
  'completed',
  'failed',
]
const TERMINAL_PHASES: ContributionImportProgressPhase[] = [
  'completed',
  'failed',
]

function importIsActive(importProgress?: ContributionImportProgress): boolean {
  if (!importProgress) {
    return false
  }
  if (TERMINAL_STATUSES.includes(importProgress.status)) {
    return false
  }
  if (TERMINAL_PHASES.includes(importProgress.phase)) {
    return false
  }
  return (
    importProgress.status === 'queued' || importProgress.status === 'processing'
  )
}

function jobResponseIsActive(data?: GetLatestJobResponse): boolean {
  if (!data) {
    return false
  }
  // Stay active while EITHER the import phase OR the underlying PGBoss job is
  // running. A finished import can still leave the job applying work, so keep
  // polling/isProcessing until the job state is also idle. importIsActive
  // null-guards its argument.
  return importIsActive(data.import) || jobIsActive(data.job)
}

function historyHasActiveJobs(jobs?: ContributionJobHistoryItem[]): boolean {
  if (!jobs) {
    return false
  }
  return jobs.some(
    (job) => job.status === 'queued' || job.status === 'processing'
  )
}

export default function useContributionsJobs(
  customerId: string,
  onJobComplete?: () => void | Promise<void>
) {
  const getLatestCustomerContributionJobs = () =>
    useQuery({
      queryKey: ['getLatestContributionJobs', customerId],
      queryFn: () => getLatestContributionJob(customerId),
      enabled: !!customerId,
    })

  const getAllCustomerContributionJobs = () =>
    useQuery({
      queryKey: ['getAllContributionJobs', customerId],
      queryFn: () => getContributionJobs(customerId),
      enabled: !!customerId,
      refetchInterval: (query) =>
        historyHasActiveJobs(query.state.data) ? 5000 : false,
    })

  const getContributionJobStatusById = (jobId: string) =>
    useQuery<GetLatestJobResponse>({
      queryKey: ['getContributionJobStatus', customerId, 'jobid', jobId],
      queryFn: () => getContributionJobStatus(customerId, jobId),
      enabled: !!customerId && !!jobId,
      refetchInterval: (query) => {
        if (!query.state.data) return 3000
        return jobResponseIsActive(query.state.data) ? 3000 : false
      },
    })

  const applyContributionModel = (customerId: string) =>
    useMutation({
      mutationFn: async ({
        customerId,
        modelId,
        benchmarkId,
      }: {
        customerId: string
        modelId: number
        benchmarkId?: string
      }) => {
        return postApplyContributionModel(customerId, modelId, benchmarkId)
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['getAllContributionJobs', customerId],
        })
        queryClient.invalidateQueries({
          queryKey: ['getLatestContributionJobs', customerId],
        })
      },
    })

  // State managed internally by the hook
  const [manuallyTriggeredJobId, setManuallyTriggeredJobId] = useState<
    string | null
  >(null)
  const previousIsProcessingRef = useRef<boolean>(false)

  /**
   * Polls contribution jobs with a single source of truth for isProcessing
   * Manages job state internally and calls onJobComplete callback when jobs finish
   */
  const useContributionJobPolling = () => {
    const latestJobsQuery = getLatestCustomerContributionJobs()

    // Determine which job to poll
    const jobIdToUse =
      manuallyTriggeredJobId || latestJobsQuery.data?.jobTracking.jobId

    // Poll the job status
    const jobStatusQuery = getContributionJobStatusById(jobIdToUse || '')

    // Single source of truth: isProcessing
    // Job is processing if we have a jobId and the job state is active
    const isProcessing =
      !!jobIdToUse && jobStatusQuery.data
        ? jobResponseIsActive(jobStatusQuery.data)
        : !!jobIdToUse // Consider processing if we have a jobId but no data yet

    return {
      isProcessing,
      jobResponse: jobStatusQuery.data,
      isLoadingJobs: latestJobsQuery.isLoading,
      jobId: jobIdToUse,
    }
  }

  const { isProcessing, jobResponse, isLoadingJobs, jobId } =
    useContributionJobPolling()

  // Handle job completion: call callback and clear manual job ID
  useEffect(() => {
    const wasProcessing = previousIsProcessingRef.current
    const isNowComplete = wasProcessing && !isProcessing

    if (isNowComplete && manuallyTriggeredJobId) {
      // Job just completed, execute callback if provided
      if (onJobComplete) {
        Promise.resolve(onJobComplete())
          .then(() => {
            setManuallyTriggeredJobId(null)
          })
          .catch((error) => {
            console.error('Error in onJobComplete callback:', error)
            // Clear manual job ID even on error to prevent stuck state
            setManuallyTriggeredJobId(null)
          })
      } else {
        setManuallyTriggeredJobId(null)
      }
    }

    previousIsProcessingRef.current = isProcessing
  }, [isProcessing, manuallyTriggeredJobId, onJobComplete])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setManuallyTriggeredJobId(null)
    }
  }, [])

  return {
    getCustomerContributionJobs: getLatestCustomerContributionJobs,
    getContributionJobStatusById,
    getAllCustomerContributionJobs,
    applyContributionModel,
    // Job polling state
    isProcessing,
    jobResponse,
    isLoadingJobs,
    jobId,
    // Method to trigger a manual job
    setManuallyTriggeredJobId,
  }
}
